import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { User } from '../types';
import { getUserByTelegramId, activateReferralCode } from '../services/api';
import { getTelegramUser } from '../services/telegram';
import { colors } from '../theme/themeConfig';
import { dummyOrders } from '../data/dummyOrders';

const PAGE_TITLE = 'Приглашайте друзей и получайте бонусы!';

const ReferralPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const telegramUser = getTelegramUser();

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        
        // Get Telegram user
        const tgUser = telegramUser;
        
        if (tgUser) {
          try {
            // Get user data
            const userData = await getUserByTelegramId(tgUser.id);
            setUser(userData);
          } catch (error) {
            console.error('API error, using dummy data:', error);
            // Use dummy data if API fails
            setUser(dummyOrders[0].user);
          }
        } else {
          // Use dummy data for development/preview
          console.log('No Telegram user found, using dummy data');
          // Fallback to dummy data
          const defaultUserId = 7345174833;
          const userData = await getUserByTelegramId(defaultUserId);
          setUser(userData);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        // Fallback to dummy data
        const defaultUserId = 7345174833;
        const userData = await getUserByTelegramId(defaultUserId);
        setUser(userData);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserData();
  }, []);

  const handleCopyReferralCode = () => {
    if (user?.referralCode) {
      navigator.clipboard.writeText(user.referralCode)
        .then(() => {
          showSnackbar('Ссылка скопирована в буфер обмена', 'success');
        })
        .catch(() => {
          showSnackbar('Не удалось скопировать ссылку', 'error');
        });
    }
  };

  const handleActivateReferralCode = async () => {
    if (!referralCode.trim()) {
      showSnackbar('Введите реферальную ссылку', 'error');
      return;
    }

    try {
      // Get Telegram user ID
      const tgUser = telegramUser || getTelegramUser();
      const telegramId = tgUser?.id || 7345174833; // Fallback to default ID if no Telegram user
      
      // Call API function with telegramId
      const response = await activateReferralCode(telegramId, referralCode);
      
      if (response.success) {
        showSnackbar('Реферальная ссылка активирована', 'success');
        setReferralCode('');
        
        // Refresh user data to show updated referral status
        if (user) {
          try {
            const updatedUser = await getUserByTelegramId(telegramId);
            setUser(updatedUser);
          } catch (error) {
            console.error('Error refreshing user data:', error);
          }
        }
      } else {
        showSnackbar(response.message || 'Ошибка при активации ссылки', 'error');
      }
    } catch (error) {
      console.error('Error activating referral code:', error);
      showSnackbar('Ошибка при активации ссылки', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: colors.background.default,
        minHeight: '100vh',
        padding: isMobile ? '16px 16px 80px' : '24px 24px 80px',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {/* Page title */}
      <Typography
        variant="h1"
        align="center"
        sx={{
          fontSize: '32px',
          fontWeight: 600,
          color: colors.text.primary,
          mt: 2,
        }}
      >
        {PAGE_TITLE}
      </Typography>

      {/* Referral info */}
      <Box sx={{ mt: 2 }}>
        <Typography
          align="center"
          sx={{
            color: colors.text.secondary,
            fontSize: '16px',
            mb: 1,
          }}
        >
          Делитесь своей реферальной ссылкой с друзьями и вы будете получать <span style={{ color: '#7A288A', fontWeight: 'bold' }}>11%</span> от бонусов за покупки ваших друзей
        </Typography>
      </Box>

      {/* Referral code display */}
      <Paper
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          padding: '2px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
        }}
      >
        <Typography
          sx={{
            color: colors.text.primary,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 500,
            ml: 2,
          }}
        >
          {user?.referralCode || 'ae42ef32-1d9c-4ad0-9f3f-e0f85006f8aa'}
        </Typography>
        
        <IconButton
          onClick={handleCopyReferralCode}
          sx={{
            color: colors.text.secondary,
            '&:hover': {
              color: '#7A288A',
            },
          }}
        >
          <ContentCopyIcon />
        </IconButton>
      </Paper>

      {/* Enter referral code section */}
      <Box sx={{ mt: 1 }}>
        <Typography
          align="center"
          variant="h3"
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: colors.text.primary,
            mb: 2,
          }}
        >
          Введите реферальную ссылку, чтобы получить бонусы
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            mt: 3,
          }}
        >
          <TextField
            fullWidth
            placeholder="Введите ссылку"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value)}
            InputProps={{
              sx: {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                color: colors.text.primary,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#7A288A',
                },
              },
            }}
            sx={{
              flex: isMobile ? 'auto' : 2,
            }}
          />
          
          <Button
            variant="contained"
            onClick={handleActivateReferralCode}
            sx={{
              backgroundColor: '#7A288A',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '12px',
              padding: '12px 24px',
              '&:hover': {
                backgroundColor: '#6c2085',
              },
              flex: isMobile ? 'auto' : 1,
            }}
          >
            Активировать
          </Button>
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          width: { xs: '90%', sm: '80%', md: '60%' },
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '600px',
          },
          bottom: { xs: 16, sm: 24 },
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backgroundColor: snackbarSeverity === 'success' ? '#4CAF50' : '#F44336',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 500,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              marginRight: '12px',
              fontSize: '24px',
            },
            '& .MuiAlert-message': {
              padding: '8px 0',
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferralPage;
